import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Modal, { ModalFooter } from '@amzn/meridian/modal'
import Row from '@amzn/meridian/row'
import Select, { SelectOption } from '@amzn/meridian/select'
import Button from '@amzn/meridian/button'
import { getLocalizationContext, applyDocumentLocale } from '@amzn/jwo-store-management-platform-components'
import { t } from '@src/translationHelper'

import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { Locale, localeExpanded, setLocale } from '@atredux/slices/localeSlice'
import { useBundle, useLocalizationContext } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { setStoredLocale } from '@src/i18n/localeSession'

interface IChangeLanguageModalProps {
    open: boolean
    setOpen: (open: boolean) => void
}

const ChangeLanguageModal = ({
    open,
    setOpen,
}: Readonly<IChangeLanguageModalProps>): React.ReactElement => {
    const [bundle] = useBundle(BUNDLE_NAME)
    const onClose = useCallback(() => setOpen(false), [])
    const [displayedLocale, setDisplayedLocale] = useState(useSelector(getSelectedLocale))
    const dispatch = useDispatch()
    let currentLocale = useSelector(getSelectedLocale)
    const { setLocalizationContext } = useLocalizationContext()

    const changeLocale = () => {
        dispatch(setLocale(displayedLocale))
        currentLocale = displayedLocale
        setLocalizationContext(getLocalizationContext(displayedLocale))
        // Persist so the choice survives a reload, and republish <html lang> so
        // assistive tech and the message-bundle resolver both see the new
        // language rather than the one this session started with.
        setStoredLocale(displayedLocale)
        applyDocumentLocale(displayedLocale)
        setOpen(false)
    }

    const cancelChangeLanguage = () => {
        setOpen(false)
        setDisplayedLocale(currentLocale)
    }

    return (
        <Modal
            title={t(bundle, 'ihm-associatetoolsbase-select-locale')}
            open={open}
            onClose={onClose}
            scrollContainer="viewport"
            closeLabel={t(bundle, 'jsma-web-common-close')}
        >
            <Row>
                <Select
                    value={displayedLocale}
                    onChange={setDisplayedLocale}
                    placeholder={t(bundle, 'ihm-associatetoolsbase-select-language')}
                    width="100%"
                    size="large"
                    data-testid="locale-select"
                >
                    {Object.keys(Locale).map((locale: Locale) => {
                        return <SelectOption key={locale} value={locale} label={localeExpanded[locale]} />
                    })}
                </Select>
            </Row>
            <ModalFooter>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="large" onClick={cancelChangeLanguage}>
                        {t(bundle, 'migrated-ihm-seaward-bluetooth-cancel-button')}
                    </Button>
                    <Button type="primary" size="large" onClick={changeLocale}>
                        {t(bundle, 'migrated-ihm-seaward-change-button')}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    )
}

export default ChangeLanguageModal
